import { Button, makeStyles } from "@material-ui/core";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import * as Sentry from "@sentry/react";
import React, { Dispatch, SetStateAction } from "react";
import { Controller, useForm } from "react-hook-form";
import { postDataV2, updateDataV2 } from "../../../../../api/utils";
import { ErrorMsg } from "../../../../../components/AppMessages";
import { TORCH_RED, TORCH_RED_BRIGHT, __local__ } from "../../../../../constants";
import { View } from "../../../../../constants/interface/admin/common";

interface Props {
  open: boolean;
  toggleOpen: () => void;
  setUserViews: Dispatch<SetStateAction<Array<any>>>;
  selectedUserView: any | undefined;
  views: View[];
}

type FormData = {
  name: string;
  views: View[];
};

// Defined once at module scope (rather than calling makeStyles({...})() inline on every render)
// - the inline pattern creates a brand new style-sheet hook per render, which under React 18
// crashes on unmount with "Cannot read properties of undefined (reading 'refs')" in
// @material-ui/styles' detach cleanup.
const useStyles = makeStyles({
  button: {
    backgroundColor: TORCH_RED,
    color: "#FFF !important",
    borderRadius: "0px !important",
    border: "none !important",
    marginRight: "10px",
    paddingTop: "8px !important",
    paddingBottom: "8px !important",
    "&:hover": {
      backgroundColor: `${TORCH_RED_BRIGHT} !important`,
      border: "none !important",
    },
  },
});

const UserViewFormDialog = (props: Props) => {
  const { open, toggleOpen, setUserViews, selectedUserView, views } = props;
  const { control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      name: selectedUserView?.name,
      views: selectedUserView?.views ?? [],
    },
  });

  const classes = useStyles();

  const submitHandler = (values: FormData) => {
    if (selectedUserView?.id) {
      const endpoint = `/user_service/update-view-group${__local__ ? "/" + selectedUserView.id : "?id=" + selectedUserView.id}`;
      updateDataV2(endpoint, values).then((res) => {
        if (!res.error) {
          setUserViews((prevState) =>
            prevState.map((userView) => {
              if (userView.id !== res.id) {
                return userView;
              }
              return res;
            })
          );
          toggleOpen();
        } else {
          console.log(res.error);
        }
      });
    } else {
      postDataV2("/user_service/create-view-group", values).then((res) => {
        if (!res.error) {
          setUserViews((prevState) => [...prevState, res]);
          toggleOpen();
        } else {
          console.log(res.error);
        }
      });
    }
  };

  return (
    <Sentry.ErrorBoundary fallback={<ErrorMsg />}>
      <Dialog onClose={toggleOpen} open={open} scroll="paper" fullWidth>
        <DialogTitle>Create new entry</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item md={12}>
              <FormControl fullWidth>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="Name"
                      id="user-view-name"
                      type="string"
                      value={value}
                      onChange={onChange}
                      variant="outlined"
                      required
                      inputProps={{
                        "data-test-id": "user-view-name-input",
                      }}
                    />
                  )}
                  defaultValue=""
                />
              </FormControl>
            </Grid>

            <Grid item md={12}>
              <FormControl fullWidth variant="outlined">
                <Controller
                  name="views"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      multiple
                      id="user-views-view-select"
                      options={views}
                      getOptionLabel={(option) => option.view}
                      getOptionSelected={(option, value) => option.id === value.id}
                      value={value}
                      onChange={(_, data) => onChange(data)}
                      renderTags={(value: View[], getTagProps) =>
                        value.map((option: View, index: number) => <Chip key={index} label={option.view} {...getTagProps({ index })} />)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Views"
                          required
                          InputProps={{ ...params.InputProps, ...{ "data-test-id": "user-view-views-input" } }}
                        />
                      )}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            className={classes.button}
            variant="outlined"
            type="submit"
            onClick={handleSubmit(submitHandler)}
            disableElevation
            data-test-id={`${selectedUserView?.id ? "btn-update-user-view" : "btn-create-user-view"}`}
          >
            {selectedUserView?.id ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Sentry.ErrorBoundary>
  );
};

export default UserViewFormDialog;
